<!-- views/ProfileView.vue -->
<template>
  <div class="profile-view">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading profile...</p>
    </div>

    <div v-else-if="user" class="profile-container">
      <!-- Profile Header -->
      <div class="profile-header">
        <div class="cover-photo">
          <img
            v-if="user.coverPhoto"
            :src="user.coverPhoto"
            alt="Cover photo"
          />
          <div v-else class="cover-placeholder"></div>
          <button class="edit-cover-btn" @click="uploadCoverPhoto">
            📷 Edit Cover
          </button>
        </div>

        <div class="profile-info">
          <div class="avatar-section">
            <div class="avatar-wrapper">
              <img
                v-if="user.photoURL"
                :src="user.photoURL"
                :alt="user.displayName"
                class="avatar"
              />
              <div v-else class="avatar-placeholder">
                {{ userInitials }}
              </div>
              <button class="edit-avatar-btn" @click="uploadAvatar">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"
                  />
                  <polygon points="18 2 22 6 12 16 8 16 8 12 18 2" />
                </svg>
              </button>
            </div>
          </div>

          <div class="user-details">
            <h1>{{ user.displayName }}</h1>
            <p class="bio">
              {{
                user.bio || "No bio yet. Add one to tell people about yourself!"
              }}
            </p>
            <div class="user-meta">
              <span class="meta-item">
                <span class="icon">📍</span>
                {{ user.location || "Location not set" }}
              </span>
              <span class="meta-item">
                <span class="icon">🎓</span>
                {{ user.role === "creator" ? "Course Creator" : "Learner" }}
              </span>
              <span class="meta-item">
                <span class="icon">📅</span>
                Joined {{ formatDate(user.createdAt) }}
              </span>
            </div>
          </div>

          <div class="profile-stats">
            <div class="stat" @click="showFollowers = true">
              <span class="number">{{ user.followers?.length || 0 }}</span>
              <span class="label">Followers</span>
            </div>
            <div class="stat" @click="showFollowing = true">
              <span class="number">{{ user.following?.length || 0 }}</span>
              <span class="label">Following</span>
            </div>
            <div class="stat">
              <span class="number">{{ user.createdCourses?.length || 0 }}</span>
              <span class="label">Courses</span>
            </div>
            <div class="stat">
              <span class="number">{{
                user.enrolledCourses?.length || 0
              }}</span>
              <span class="label">Learning</span>
            </div>
          </div>

          <div class="profile-actions">
            <button class="btn-primary" @click="editProfile = true">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"
                />
                <polygon points="18 2 22 6 12 16 8 16 8 12 18 2" />
              </svg>
              Edit Profile
            </button>
            <button class="btn-secondary" @click="$router.push('/studio')">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Create Course
            </button>
          </div>
        </div>
      </div>

      <!-- Profile Tabs -->
      <div class="profile-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="tab"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          {{ tab.icon }} {{ tab.label }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Courses Tab -->
        <div v-if="activeTab === 'courses'" class="courses-tab">
          <div v-if="createdCourses.length === 0" class="empty-state">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <h3>No courses created yet</h3>
            <p>Start sharing your knowledge by creating your first course!</p>
            <button class="btn-primary" @click="$router.push('/studio')">
              Create Course
            </button>
          </div>
          <div v-else class="courses-grid">
            <CourseCard
              v-for="course in createdCourses"
              :key="course.id"
              :course="course"
              @click="$router.push(`/course/${course.id}`)"
            />
          </div>
        </div>

        <!-- Learning Tab -->
        <div v-if="activeTab === 'learning'" class="learning-tab">
          <div v-if="enrolledCourses.length === 0" class="empty-state">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <h3>No courses enrolled yet</h3>
            <p>Explore courses and start your learning journey!</p>
            <button class="btn-primary" @click="$router.push('/explore')">
              Explore Courses
            </button>
          </div>
          <div v-else class="courses-grid">
            <CourseCard
              v-for="course in enrolledCourses"
              :key="course.id"
              :course="course"
              :showProgress="true"
              @click="$router.push(`/course/${course.id}`)"
            />
          </div>
        </div>

        <!-- Social Links Tab -->
        <div v-if="activeTab === 'social'" class="social-tab">
          <div class="social-links-grid">
            <div
              v-for="(url, platform) in user.socialLinks"
              :key="platform"
              class="social-link-item"
            >
              <span class="social-icon">{{ getSocialIcon(platform) }}</span>
              <a :href="url" target="_blank" class="social-url">{{ url }}</a>
            </div>
            <div
              v-if="
                !user.socialLinks || Object.keys(user.socialLinks).length === 0
              "
              class="empty-social"
            >
              <p>No social links added yet</p>
              <button class="btn-secondary" @click="editProfile = true">
                Add Social Links
              </button>
            </div>
          </div>
        </div>

        <!-- Settings Tab -->
        <div v-if="activeTab === 'settings'" class="settings-tab">
          <div class="settings-card">
            <h3>Account Settings</h3>
            <div class="settings-group">
              <label class="toggle-label">
                <span>Email Notifications</span>
                <input type="checkbox" v-model="settings.emailNotifications" />
                <span class="toggle-slider"></span>
              </label>
              <label class="toggle-label">
                <span>Private Profile</span>
                <input type="checkbox" v-model="settings.privateProfile" />
                <span class="toggle-slider"></span>
              </label>
              <button class="btn-primary" @click="saveSettings">
                Save Settings
              </button>
            </div>
          </div>

          <div class="settings-card danger">
            <h3>Danger Zone</h3>
            <button class="btn-danger" @click="deleteAccount">
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Profile Modal -->
    <EditProfileModal
      v-if="editProfile"
      :user="user"
      @close="editProfile = false"
      @updated="refreshProfile"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useCourseStore } from "@/stores/course";
import CourseCard from "@/components/courses/CourseCard.vue";
import EditProfileModal from "@/components/profile/EditProfileModal.vue";

const authStore = useAuthStore();
const courseStore = useCourseStore();

const loading = ref(true);
const editProfile = ref(false);
const showFollowers = ref(false);
const showFollowing = ref(false);
const activeTab = ref("courses");

const user = computed(() => authStore.user);
const userInitials = computed(() => {
  if (!user.value?.displayName) return "?";
  return user.value.displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
});

const createdCourses = computed(() =>
  courseStore.courses.filter((c) => c.creatorId === user.value?.id),
);
const enrolledCourses = computed(() =>
  courseStore.courses.filter((c) => c.isEnrolled),
);

const settings = ref({
  emailNotifications: true,
  privateProfile: false,
});

const tabs = [
  { id: "courses", label: "My Courses", icon: "📚" },
  { id: "learning", label: "Learning", icon: "🎓" },
  { id: "social", label: "Social Links", icon: "🔗" },
  { id: "settings", label: "Settings", icon: "⚙️" },
];

onMounted(async () => {
  if (user.value) {
    await courseStore.fetchCreatorCourses(user.value.id);
  }
  loading.value = false;
});

const formatDate = (timestamp) => {
  if (!timestamp) return "Recently";
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getSocialIcon = (platform) => {
  const icons = {
    github: "🐙",
    website: "🌐",
    linkedin: "💼",
    twitter: "🐦",
    youtube: "▶️",
    instagram: "📸",
  };
  return icons[platform] || "🔗";
};

const uploadAvatar = () => {
  // Open file picker for avatar upload
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Upload to Firebase Storage
      // const url = await uploadImage(file);
      // await authStore.updateUserProfile({ photoURL: url });
    }
  };
  input.click();
};

const uploadCoverPhoto = () => {
  // Similar to avatar upload
};

const refreshProfile = () => {
  // Reload profile data
};

const saveSettings = () => {
  // Save user settings
};

const deleteAccount = () => {
  if (
    confirm(
      "Are you sure you want to delete your account? This action cannot be undone!",
    )
  ) {
    // Delete account
  }
};
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;
$primary-blue: #001829;

.profile-view {
  margin-top: 72px;
  min-height: 100vh;
  background: #f0f2f5;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e9ecef;
    border-top: 4px solid $primary-orange;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  p {
    margin-top: 16px;
    color: #868e96;
  }
}

.profile-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.profile-header {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.cover-photo {
  position: relative;
  height: 200px;
  background: linear-gradient(135deg, $primary-blue, #003355);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .cover-placeholder {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, $primary-blue, #003355);
  }

  .edit-cover-btn {
    position: absolute;
    bottom: 16px;
    right: 16px;
    padding: 8px 16px;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    backdrop-filter: blur(4px);

    &:hover {
      background: rgba(0, 0, 0, 0.8);
    }
  }
}

.profile-info {
  padding: 24px;
  position: relative;

  .avatar-section {
    position: relative;
    margin-top: -80px;
    display: inline-block;

    .avatar-wrapper {
      position: relative;

      .avatar {
        width: 120px;
        height: 120px;
        border-radius: 50%;
        border: 4px solid #fff;
        object-fit: cover;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .avatar-placeholder {
        width: 120px;
        height: 120px;
        border-radius: 50%;
        border: 4px solid #fff;
        background: $primary-orange;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 36px;
        font-weight: 700;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .edit-avatar-btn {
        position: absolute;
        bottom: 4px;
        right: 4px;
        width: 32px;
        height: 32px;
        background: $primary-orange;
        color: #fff;
        border: 2px solid #fff;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;

        &:hover {
          transform: scale(1.1);
        }
      }
    }
  }

  .user-details {
    margin-top: 16px;

    h1 {
      margin: 0 0 8px 0;
      font-size: 28px;
      color: #1a1a1a;
    }

    .bio {
      color: #495057;
      font-size: 16px;
      margin: 0 0 12px 0;
      line-height: 1.6;
    }

    .user-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;

      .meta-item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        color: #868e96;

        .icon {
          font-size: 18px;
        }
      }
    }
  }

  .profile-stats {
    display: flex;
    gap: 32px;
    margin: 16px 0;
    padding: 16px 0;
    border-top: 1px solid #f1f3f5;
    border-bottom: 1px solid #f1f3f5;

    .stat {
      text-align: center;
      cursor: pointer;

      .number {
        display: block;
        font-weight: 700;
        font-size: 20px;
        color: #1a1a1a;
      }

      .label {
        font-size: 14px;
        color: #868e96;
      }

      &:hover {
        .number {
          color: $primary-orange;
        }
      }
    }
  }

  .profile-actions {
    display: flex;
    gap: 12px;
    margin-top: 16px;

    .btn-primary {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 24px;
      background: $primary-orange;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: darken($primary-orange, 10%);
      }
    }

    .btn-secondary {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 24px;
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      color: #495057;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: #f1f3f5;
      }
    }
  }
}

.profile-tabs {
  display: flex;
  background: #fff;
  border-radius: 0 0 16px 16px;
  padding: 0 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .tab {
    padding: 16px 20px;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    font-size: 14px;
    font-weight: 600;
    color: #868e96;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
      color: #1a1a1a;
    }

    &.active {
      color: $primary-orange;
      border-bottom-color: $primary-orange;
    }
  }
}

.tab-content {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: 400px;
}

.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;

  svg {
    color: #adb5bd;
    margin-bottom: 16px;
  }

  h3 {
    color: #1a1a1a;
    margin: 0 0 8px 0;
  }

  p {
    color: #868e96;
    margin-bottom: 24px;
  }

  .btn-primary {
    padding: 10px 24px;
    background: $primary-orange;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      background: darken($primary-orange, 10%);
    }
  }
}

.social-links-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;

  .social-link-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #f8f9fa;
    border-radius: 8px;

    .social-icon {
      font-size: 24px;
    }

    .social-url {
      color: $primary-orange;
      text-decoration: none;
      word-break: break-all;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .empty-social {
    text-align: center;
    padding: 40px;
    color: #868e96;

    .btn-secondary {
      margin-top: 12px;
      padding: 8px 16px;
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      cursor: pointer;

      &:hover {
        background: #f1f3f5;
      }
    }
  }
}

.settings-tab {
  .settings-card {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 16px;

    h3 {
      margin: 0 0 16px 0;
      color: #1a1a1a;
    }

    .settings-group {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .toggle-label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;

      input {
        display: none;
      }

      .toggle-slider {
        width: 48px;
        height: 26px;
        background: #ccc;
        border-radius: 13px;
        position: relative;
        transition: all 0.3s ease;
        flex-shrink: 0;

        &::after {
          content: "";
          position: absolute;
          top: 2px;
          left: 2px;
          width: 22px;
          height: 22px;
          background: #fff;
          border-radius: 50%;
          transition: all 0.3s ease;
        }
      }

      input:checked + .toggle-slider {
        background: $primary-orange;

        &::after {
          transform: translateX(22px);
        }
      }
    }

    .btn-primary {
      padding: 10px 24px;
      background: $primary-orange;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      align-self: flex-start;

      &:hover {
        background: darken($primary-orange, 10%);
      }
    }

    &.danger {
      border: 2px solid #dc3545;

      .btn-danger {
        padding: 10px 24px;
        background: #dc3545;
        color: #fff;
        border: none;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;

        &:hover {
          background: darken(#dc3545, 10%);
        }
      }
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
